import { useEffect, useMemo, useState } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!response.ok) {
    const detail = typeof data === 'string'
      ? data
      : data?.message || data?.hint || text;
    throw new Error(`${label} failed (${response.status}): ${detail}`);
  }

  return data;
}

async function rpc(name, body, label) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${name}`,
    {
      method: 'POST',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    }
  );
  return readJson(response, label);
}

async function fetchRows(path, label) {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: apiHeaders()
  });
  return readJson(response, label);
}

function studentLabel(enrolment) {
  const student = enrolment?.students;
  const name =
    student?.english_name ||
    student?.chinese_name ||
    'Student';
  const code = student?.student_code || '';
  return code ? `${code} — ${name}` : name;
}

function assignmentLabel(assignment, allAssignments, assignmentTypes) {
  if (!assignment) return 'Assignment';

  const type = assignmentTypes.find(
    item => String(item.id) === String(assignment.assignment_type_id)
  );

  const sameType = allAssignments.filter(
    item =>
      String(item.assignment_type_id) ===
      String(assignment.assignment_type_id)
  );

  return `${type?.name || assignment.assignment_types?.name || 'Assignment'}${
    sameType.length > 1 ? ` ${assignment.instance_number}` : ''
  }`;
}

function assignmentKind(assignment, assignmentTypes) {
  if (!assignment) return '';

  const type = (assignmentTypes || []).find(
    item =>
      String(item.id) ===
      String(assignment.assignment_type_id)
  );

  return (
    type?.assignment_kind ||
    assignment.assignment_types?.assignment_kind ||
    ''
  );
}

function reviewState(review, commentCount) {
  if (!review) return 'Not started';
  if (review.submitted) return 'Submitted';
  if (
    String(review.overall_comment || '').trim() ||
    Number(commentCount || 0) > 0
  ) {
    return 'Draft started';
  }
  return 'Not started';
}

function PeerReviewInstanceManager({
  course,
  assignment,
  allCourseAssignments,
  assignmentTypes,
  enrolments,
  orderedTypeIds
}) {
  const [config, setConfig] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [targetSubmissions, setTargetSubmissions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [commentCounts, setCommentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
  const [targetDraftId, setTargetDraftId] = useState('');
  const [
    structuredInstanceDraftId,
    setStructuredInstanceDraftId
  ] = useState('');
  const [structuredInstances, setStructuredInstances] = useState([]);
  const [
    loadingStructuredInstances,
    setLoadingStructuredInstances
  ] = useState(false);

  const [
    targetGroupMemberships,
    setTargetGroupMemberships
  ] = useState([]);

  const [
    targetGroupWorkspaces,
    setTargetGroupWorkspaces
  ] = useState([]);

  const [
    targetGroupLabels,
    setTargetGroupLabels
  ] = useState([]);


  const activeEnrolments = useMemo(
    () =>
      (enrolments || [])
        .filter(enrolment => enrolment?.active !== false)
        .filter(enrolment => !enrolment?.students?.is_master)
        .slice()
        .sort((a, b) =>
          studentLabel(a).localeCompare(studentLabel(b))
        ),
    [enrolments]
  );

  const targetAssignments = useMemo(() => {
    const typeIndex = new Map(
      (orderedTypeIds || []).map((id, index) => [String(id), index])
    );

    return (allCourseAssignments || [])
      .filter(candidate => candidate.id !== assignment.id)
      .filter(candidate => {
        const type = assignmentTypes.find(
          item => String(item.id) === String(candidate.assignment_type_id)
        );

        return (
          (type?.requires_submission ?? true) &&
          type?.assignment_kind !== 'peer_review'
        );
      })
      .slice()
      .sort((a, b) => {
        const aIndex = typeIndex.get(String(a.assignment_type_id)) ?? 999999;
        const bIndex = typeIndex.get(String(b.assignment_type_id)) ?? 999999;

        return (
          aIndex - bIndex ||
          (Number(a.instance_number) || 0) -
            (Number(b.instance_number) || 0)
        );
      });
  }, [
    allCourseAssignments,
    assignment.id,
    assignmentTypes,
    orderedTypeIds
  ]);

  async function loadReviewStatus(currentAllocations) {
    const ids = (currentAllocations || []).map(row => row.id).filter(Boolean);

    if (ids.length === 0) {
      setReviews([]);
      setCommentCounts({});
      return;
    }

    const reviewRows = await fetchRows(
      `peer_review_submissions?allocation_id=in.(${ids.join(',')})&select=*`,
      'Loading Peer Review status'
    );

    const currentReviews = Array.isArray(reviewRows) ? reviewRows : [];
    setReviews(currentReviews);

    const reviewIds = currentReviews.map(row => row.id).filter(Boolean);
    if (reviewIds.length === 0) {
      setCommentCounts({});
      return;
    }

    const comments = await fetchRows(
      `peer_review_comments?peer_review_submission_id=in.(${reviewIds.join(',')})&select=id,peer_review_submission_id`,
      'Loading Peer Review comment counts'
    );

    const counts = {};
    (Array.isArray(comments) ? comments : []).forEach(comment => {
      counts[comment.peer_review_submission_id] =
        (counts[comment.peer_review_submission_id] || 0) + 1;
    });
    setCommentCounts(counts);
  }

    async function loadTargetSubmissions(
    targetAssignmentId,
    targetStructuredInstanceId = null
  ) {
    if (!targetAssignmentId) {
      setTargetSubmissions([]);
      return [];
    }

    const instanceFilter =
      targetStructuredInstanceId
        ? `&structured_form_instance_id=eq.${encodeURIComponent(
            targetStructuredInstanceId
          )}`
        : '';

    const rows = await fetchRows(
      `assignment_submissions?course_assignment_id=eq.${encodeURIComponent(
        targetAssignmentId
      )}&submitted=eq.true${instanceFilter}&select=id,enrolment_id,file_name,review_pdf_drive_file_id,review_pdf_file_name,course_assignment_schedule_item_id,structured_form_instance_id,structured_form_snapshot,submitted_at`,
      'Loading submitted target work'
    );

    const current = Array.isArray(rows) ? rows : [];
    setTargetSubmissions(current);
    return current;
  }


  async function reload() {
    const configRows = await fetchRows(
      `peer_review_configs?course_assignment_id=eq.${assignment.id}&select=*&limit=1`,
      'Loading Peer Review settings'
    );

        const currentConfig = configRows?.[0] || null;
    setConfig(currentConfig);
    setTargetDraftId(
      currentConfig?.target_course_assignment_id || ''
    );
    setStructuredInstanceDraftId(
      currentConfig?.target_structured_instance_id || ''
    );


    const allocationRows = await fetchRows(
      `peer_review_allocations?peer_review_course_assignment_id=eq.${assignment.id}&select=*&order=created_at.asc`,
      'Loading Peer Review allocations'
    );

    const currentAllocations = Array.isArray(allocationRows)
      ? allocationRows
      : [];

    setAllocations(currentAllocations);

    await Promise.all([
            loadTargetSubmissions(
        currentConfig?.target_course_assignment_id,
        currentConfig?.target_structured_instance_id
      ),
      loadReviewStatus(currentAllocations)

    ]);
  }

  // reload() is intentionally reused by save/randomise/finalise/unsubmit actions.
  // The initial load should rerun only when the Peer Review instance changes.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        await reload();
      } catch (err) {
        console.error('Peer Review manager load failed:', err);
        if (!cancelled) {
          setError(err?.message || 'Could not load Peer Review settings.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [assignment.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

    useEffect(() => {
    let cancelled = false;

    const candidate =
      targetAssignments.find(
        item =>
          String(item.id) ===
          String(targetDraftId)
      ) || null;

    const kind =
      assignmentKind(
        candidate,
        assignmentTypes
      );

    if (
      !candidate ||
      kind !== 'structured_form'
    ) {
      setStructuredInstances([]);
      setTargetGroupMemberships([]);
      setTargetGroupWorkspaces([]);
      setTargetGroupLabels([]);
      setLoadingStructuredInstances(false);
      return undefined;
    }

    const grouped =
      candidate.groups_enabled === true;

    setLoadingStructuredInstances(true);

    const instancePromise =
      fetchRows(
        `course_structured_instances?course_assignment_id=eq.${encodeURIComponent(
          candidate.id
        )}&select=id,instance_order,open_date,close_date&order=instance_order.asc`,
        'Loading Structured Form Instances'
      );

    const membershipPromise =
      grouped
        ? fetchRows(
            `assignment_group_memberships?course_assignment_id=eq.${encodeURIComponent(
              candidate.id
            )}&select=enrolment_id,group_number`,
            'Loading Structured Form group memberships'
          )
        : Promise.resolve([]);

    const workspacePromise =
      grouped
        ? fetchRows(
            `structured_form_group_workspaces?course_assignment_id=eq.${encodeURIComponent(
              candidate.id
            )}&select=group_number,owner_enrolment_id`,
            'Loading Structured Form group workspaces'
          )
        : Promise.resolve([]);

    const labelPromise =
      grouped
        ? fetchRows(
            `assignment_group_labels?course_assignment_id=eq.${encodeURIComponent(
              candidate.id
            )}&select=group_number,name`,
            'Loading Structured Form group names'
          )
        : Promise.resolve([]);

    Promise.all([
      instancePromise,
      membershipPromise,
      workspacePromise,
      labelPromise
    ])
      .then(([
        instances,
        memberships,
        workspaces,
        labels
      ]) => {
        if (cancelled) {
          return;
        }

        setStructuredInstances(
          Array.isArray(instances)
            ? instances
            : []
        );

        setTargetGroupMemberships(
          Array.isArray(memberships)
            ? memberships
            : []
        );

        setTargetGroupWorkspaces(
          Array.isArray(workspaces)
            ? workspaces
            : []
        );

        setTargetGroupLabels(
          Array.isArray(labels)
            ? labels
            : []
        );
      })
      .catch(err => {
        console.error(
          'Loading Structured Form Peer Review target failed:',
          err
        );

        if (!cancelled) {
          setStructuredInstances([]);
          setTargetGroupMemberships([]);
          setTargetGroupWorkspaces([]);
          setTargetGroupLabels([]);
          setError(
            err?.message ||
              'Could not load the Structured Form Peer Review target.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingStructuredInstances(
            false
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    targetDraftId,
    targetAssignments,
    assignmentTypes
  ]);

  const targetAssignmentId = targetDraftId || '';

  const targetAssignment =
    targetAssignments.find(
      candidate =>
        String(candidate.id) === String(targetAssignmentId)
    ) || null;

  const targetKind = assignmentKind(
    targetAssignment,
    assignmentTypes
  );

  const isStructuredTarget =
    targetKind === 'structured_form';

  const groupedStructuredTarget =
    isStructuredTarget &&
    targetAssignment?.groups_enabled === true;

  const targetStructuredInstanceId =
    structuredInstanceDraftId || '';

  const configuredTargetAssignmentId =
    config?.target_course_assignment_id || '';

  const configuredStructuredInstanceId =
    config?.target_structured_instance_id || '';

  const configMatchesDraft =
    String(configuredTargetAssignmentId) ===
      String(targetAssignmentId) &&
    (
      !isStructuredTarget ||
      String(configuredStructuredInstanceId) ===
        String(targetStructuredInstanceId)
    );

  const targetConfigReady =
    !!targetAssignmentId &&
    (
      !isStructuredTarget ||
      !!targetStructuredInstanceId
    ) &&
    configMatchesDraft;

  const anonymous =
    config?.anonymous !== false;

  const finalized =
    config?.allocation_status ===
    'finalized';

  const workspaceByOwner =
    new Map(
      targetGroupWorkspaces.map(
        row => [
          String(
            row.owner_enrolment_id
          ),
          row
        ]
      )
    );

  function targetGroupNumberForSubmission(
    submission
  ) {
    if (!groupedStructuredTarget) {
      return null;
    }

    return (
      workspaceByOwner.get(
        String(
          submission?.enrolment_id || ''
        )
      )?.group_number ||
      null
    );
  }

  function targetGroupNumberForReviewer(
    reviewerEnrolmentId
  ) {
    if (!groupedStructuredTarget) {
      return null;
    }

    return (
      targetGroupMemberships.find(
        row =>
          String(row.enrolment_id) ===
          String(reviewerEnrolmentId)
      )?.group_number ||
      null
    );
  }

  function targetGroupName(
    groupNumber
  ) {
    if (
      groupNumber === null ||
      groupNumber === undefined ||
      groupNumber === ''
    ) {
      return 'Group';
    }

    return (
      targetGroupLabels.find(
        row =>
          Number(row.group_number) ===
          Number(groupNumber)
      )?.name ||
      `Group ${groupNumber}`
    );
  }

  const reviewableSubmissions =
    targetSubmissions.filter(
      submission =>
        isStructuredTarget
          ? (
              String(
                submission
                  .structured_form_instance_id ||
                  ''
              ) ===
                String(
                  targetStructuredInstanceId ||
                    ''
                ) &&
              submission
                .structured_form_snapshot &&
              typeof submission
                .structured_form_snapshot ===
                'object' &&
              (
                !groupedStructuredTarget ||
                !!targetGroupNumberForSubmission(
                  submission
                )
              )
            )
          : !!submission
              .review_pdf_drive_file_id
    );

  const submittedGroupNumbers =
    new Set(
      groupedStructuredTarget
        ? reviewableSubmissions
            .map(
              targetGroupNumberForSubmission
            )
            .filter(Boolean)
            .map(Number)
        : []
    );

  const reviewableSubmitterIds =
    groupedStructuredTarget
      ? new Set(
          targetGroupMemberships
            .filter(
              row =>
                submittedGroupNumbers.has(
                  Number(
                    row.group_number
                  )
                )
            )
            .map(
              row =>
                row.enrolment_id
            )
        )
      : new Set(
          reviewableSubmissions.map(
            submission =>
              submission.enrolment_id
          )
        );

  const submittedStudentCount =
    groupedStructuredTarget
      ? reviewableSubmitterIds.size
      : new Set(
          targetSubmissions.map(
            submission =>
              submission.enrolment_id
          )
        ).size;

  const submittedGroupCount =
    submittedGroupNumbers.size;

  const reviewableStudentCount =
    reviewableSubmitterIds.size;


  const allocationByReviewer = new Map(
    allocations.map(row => [row.reviewer_enrolment_id, row])
  );

  const reviewByAllocation = new Map(
    reviews.map(row => [row.allocation_id, row])
  );

  const usedTargetIds = new Set(
    allocations.map(row => row.target_submission_id)
  );

  const usedRevieweeIds = new Set(
    allocations.map(row => row.reviewee_enrolment_id)
  );

    function targetSubmissionLabel(
    submission
  ) {
    const enrolment =
      activeEnrolments.find(
        item =>
          item.id ===
          submission.enrolment_id
      );

    if (isStructuredTarget) {
      const instance =
        structuredInstances.find(
          item =>
            String(item.id) ===
            String(
              submission
                .structured_form_instance_id
            )
        );

      if (groupedStructuredTarget) {
        const groupNumber =
          targetGroupNumberForSubmission(
            submission
          );

        return `${targetGroupName(
          groupNumber
        )} — Instance ${
          instance?.instance_order ||
          '?'
        } snapshot`;
      }

      return `${
        enrolment
          ? studentLabel(enrolment)
          : 'Student'
      } — Instance ${
        instance?.instance_order ||
        '?'
      } snapshot`;
    }

    const file =
      submission.file_name ||
      submission.review_pdf_file_name ||
      'Submitted work';

    return `${
      enrolment
        ? studentLabel(enrolment)
        : 'Student'
    } — ${file}`;
  }

    async function saveConfig(
    nextTarget,
    nextStructuredInstance,
    nextAnonymous
  ) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_set_config',
        {
          p_peer_review_course_assignment_id: assignment.id,
          p_target_course_assignment_id: nextTarget || null,
          p_target_structured_instance_id:
            nextStructuredInstance || null,
          p_anonymous: !!nextAnonymous
        },
        'Saving Peer Review settings'
      );

      await reload();
      setSuccess('Peer Review settings saved.');
    } catch (err) {
      console.error('Saving Peer Review settings failed:', err);
      setError(err?.message || 'Could not save Peer Review settings.');
    } finally {
      setSaving(false);
    }
  }

  async function handleTargetAssignmentChange(nextTarget) {
    setTargetDraftId(nextTarget);
    setStructuredInstanceDraftId('');
    setError('');
    setSuccess('');

    if (!nextTarget) {
      await saveConfig(null, null, anonymous);
      return;
    }

    const candidate =
      targetAssignments.find(
        item => String(item.id) === String(nextTarget)
      ) || null;

    const kind = assignmentKind(candidate, assignmentTypes);

    if (kind === 'structured_form') {
      return;
    }

    await saveConfig(nextTarget, null, anonymous);
  }

  async function handleStructuredInstanceChange(nextInstanceId) {
    setStructuredInstanceDraftId(nextInstanceId);
    setError('');
    setSuccess('');

    if (!nextInstanceId) return;

    await saveConfig(
      targetAssignmentId,
      nextInstanceId,
      anonymous
    );
  }

  async function randomize() {

        if (!targetAssignmentId) {
      setError('Choose the assignment to review first.');
      return;
    }

    if (
      isStructuredTarget &&
      !targetStructuredInstanceId
    ) {
      setError(
        'Choose the Structured Form Instance to review first.'
      );
      return;
    }

    if (!targetConfigReady) {
      setError(
        'Save the Peer Review target before randomising.'
      );
      return;
    }


    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_randomize',
        { p_peer_review_course_assignment_id: assignment.id },
        'Randomising Peer Review allocations'
      );

      await reload();
      setSuccess('Peer Review allocations randomised.');
    } catch (err) {
      console.error('Peer Review randomisation failed:', err);
      setError(err?.message || 'Could not randomise allocations.');
    } finally {
      setSaving(false);
    }
  }

  async function changeAllocation(reviewerEnrolmentId, targetSubmissionId) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_set_allocation',
        {
          p_peer_review_course_assignment_id: assignment.id,
          p_reviewer_enrolment_id: reviewerEnrolmentId,
          p_target_submission_id: targetSubmissionId || null
        },
        'Updating Peer Review allocation'
      );

      await reload();
    } catch (err) {
      console.error('Peer Review allocation update failed:', err);
      setError(err?.message || 'Could not update allocation.');
    } finally {
      setSaving(false);
    }
  }

  async function finalize() {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_finalize_allocations',
        { p_peer_review_course_assignment_id: assignment.id },
        'Finalising Peer Review allocations'
      );

      await reload();
      setSuccess('Allocations finalised and released to students.');
    } catch (err) {
      console.error('Peer Review finalisation failed:', err);
      setError(err?.message || 'Could not finalise allocations.');
    } finally {
      setSaving(false);
    }
  }

  async function unlock() {
    if (!window.confirm(
      'Unlock these allocations? Students keep any existing review work. Allocations that already contain review work are still protected from reassignment.'
    )) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_unlock_allocations',
        { p_peer_review_course_assignment_id: assignment.id },
        'Unlocking Peer Review allocations'
      );

      await reload();
      setSuccess('Allocations unlocked.');
    } catch (err) {
      setError(err?.message || 'Could not unlock allocations.');
    } finally {
      setSaving(false);
    }
  }

  async function unsubmitReview(review) {
    if (!review?.id) return;

    if (!window.confirm(
      'Unsubmit this Peer Review? The student will be able to edit and delete their existing comments again while the assignment is open.'
    )) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await rpc(
        'peer_review_instructor_unsubmit',
        {
          p_review_id: review.id,
          p_instructor_id: course?.instructor_id || null
        },
        'Unsubmitting Peer Review'
      );

      await reload();
      setSuccess('Peer Review returned to draft.');
    } catch (err) {
      setError(err?.message || 'Could not unsubmit Peer Review.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={styles.muted}>Loading Peer Review setup...</div>;
  }

  return (
    <div style={styles.instanceWrap}>
      <div style={styles.instanceHeading}>
        {assignmentLabel(assignment, allCourseAssignments, assignmentTypes)}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.settingsGrid}>
        <label style={styles.field}>
          <span style={styles.label}>Assignment to review</span>
          <select
            style={styles.select}
            value={targetAssignmentId}
            disabled={saving || finalized}
                        onChange={event =>
              handleTargetAssignmentChange(event.target.value)
            }

          >
            <option value="">Select assignment...</option>
                        {targetAssignments.map(
              candidate => (
                <option
                  key={candidate.id}
                  value={candidate.id}
                >
                  {assignmentLabel(
                    candidate,
                    allCourseAssignments,
                    assignmentTypes
                  )}
                </option>
              )
            )}

          </select>
                </label>

        {isStructuredTarget && (
          <label style={styles.field}>
            <span style={styles.label}>
              Structured Form Instance
            </span>

            <select
              style={styles.select}
              value={targetStructuredInstanceId}
              disabled={
                saving ||
                finalized ||
                loadingStructuredInstances
              }
              onChange={event =>
                handleStructuredInstanceChange(
                  event.target.value
                )
              }
            >
              <option value="">
                {loadingStructuredInstances
                  ? 'Loading Instances...'
                  : 'Select Instance...'}
              </option>

              {structuredInstances.map(instance => (
                <option
                  key={instance.id}
                  value={instance.id}
                >
                  Instance {instance.instance_order}
                </option>
              ))}
            </select>
          </label>
        )}

        <label style={styles.toggleField}>

          <input
            type="checkbox"
            checked={anonymous}
                        disabled={
              saving ||
              finalized ||
              (
                isStructuredTarget &&
                !targetStructuredInstanceId
              )
            }
            onChange={event =>
              saveConfig(
                targetAssignmentId,
                isStructuredTarget
                  ? targetStructuredInstanceId
                  : null,
                event.target.checked
              )
            }

          />
          <span>
            <strong>Anonymous peer review</strong>
            <small style={styles.toggleHint}>
              Hides identities from both reviewer and reviewed student.
            </small>
          </span>
        </label>
      </div>

            {targetAssignmentId &&
        configMatchesDraft && (
        <div style={styles.targetSummary}>
          {groupedStructuredTarget ? (
            <>
              <strong>
                {submittedGroupCount}
              </strong>{' '}
              group{
                submittedGroupCount === 1
                  ? ''
                  : 's'
              } submitted this Instance.{' '}
              <strong>
                {reviewableStudentCount}
              </strong>{' '}
              student{
                reviewableStudentCount === 1
                  ? ''
                  : 's'
              } in those groups will be in
              the default random pool.
            </>
          ) : isStructuredTarget ? (
            <>
              <strong>
                {submittedStudentCount}
              </strong>{' '}
              student{
                submittedStudentCount === 1
                  ? ''
                  : 's'
              } submitted this Instance.{' '}
              <strong>
                {reviewableStudentCount}
              </strong>{' '}
              immutable snapshot{
                reviewableStudentCount === 1
                  ? ''
                  : 's'
              } ready for review.
            </>
          ) : (
            <>
              <strong>{submittedStudentCount}</strong>{' '}
              student{submittedStudentCount === 1 ? '' : 's'} submitted.{' '}
              <strong>{reviewableStudentCount}</strong>{' '}
              have PDF/DOCX work ready for inline review.
              {submittedStudentCount > reviewableStudentCount && (
                <span style={styles.warningText}>
                  {' '}Submissions without a reviewable PDF rendition are excluded.
                </span>
              )}
            </>
          )}
        </div>
      )}

      {isStructuredTarget &&
        !targetStructuredInstanceId && (
        <div style={styles.targetSummary}>
          Choose the exact submitted Instance that students should review.
        </div>
      )}


      <div style={styles.actions}>
        {!finalized ? (
          <>
            <button
              type="button"
              style={styles.secondaryBtn}
                            disabled={saving || !targetConfigReady}

              onClick={randomize}
            >
              Randomise
            </button>
            <button
              type="button"
              style={styles.primaryBtn}
                            disabled={saving || !targetConfigReady}

              onClick={finalize}
            >
              Finalise Allocations
            </button>
          </>
        ) : (
          <>
            <span style={styles.finalizedPill}>Finalised</span>
            <button
              type="button"
              style={styles.secondaryBtn}
              disabled={saving}
              onClick={unlock}
            >
              Unlock Allocations
            </button>
          </>
        )}
      </div>

            {targetConfigReady && (
        <div style={styles.allocationTable}>

          <div style={styles.tableHeader}>
            <div>Reviewer</div>
            <div>Assigned work</div>
            <div>Status</div>
          </div>

          {activeEnrolments.map(reviewer => {
            const allocation = allocationByReviewer.get(reviewer.id);
            const review = allocation
              ? reviewByAllocation.get(allocation.id)
              : null;
            const state = reviewState(
              review,
              review ? commentCounts[review.id] : 0
            );
            const eligibleByDefault = reviewableSubmitterIds.has(reviewer.id);

            return (
              <div key={reviewer.id} style={styles.tableRow}>
                <div style={styles.reviewerCell}>
                  <div>{studentLabel(reviewer)}</div>
                  <div style={styles.reviewerMeta}>
                    {eligibleByDefault
                      ? 'Default random pool'
                      : 'Not in default random pool'}
                  </div>
                </div>

                <select
                  style={styles.allocationSelect}
                  value={allocation?.target_submission_id || ''}
                  disabled={saving || finalized}
                  onChange={event =>
                    changeAllocation(reviewer.id, event.target.value)
                  }
                >
                  <option value="">Unassigned</option>
                  {reviewableSubmissions
                    .filter(submission => {
                      if (
                        groupedStructuredTarget
                      ) {
                        const reviewerGroup =
                          targetGroupNumberForReviewer(
                            reviewer.id
                          );

                        const targetGroup =
                          targetGroupNumberForSubmission(
                            submission
                          );

                        return (
                          !!targetGroup &&
                          (
                            !reviewerGroup ||
                            Number(
                              targetGroup
                            ) !==
                              Number(
                                reviewerGroup
                              )
                          )
                        );
                      }

                      return (
                        submission.enrolment_id !==
                        reviewer.id
                      );
                    })
                    .map(submission => {
                      const usedElsewhere =
                        !groupedStructuredTarget &&
                        (
                          (
                            usedTargetIds.has(
                              submission.id
                            ) ||
                            usedRevieweeIds.has(
                              submission
                                .enrolment_id
                            )
                          ) &&
                          submission.id !==
                            allocation
                              ?.target_submission_id
                        );

                      return (
                        <option
                          key={submission.id}
                          value={submission.id}
                          disabled={usedElsewhere}
                        >
                          {targetSubmissionLabel(
                            submission
                          )}
                          {usedElsewhere
                            ? ' — already assigned'
                            : ''}
                        </option>
                      );
                    })}
                </select>

                <div style={styles.statusCell}>
                  <span
                    style={state === 'Submitted'
                      ? styles.statusSubmitted
                      : state === 'Draft started'
                        ? styles.statusDraft
                        : styles.statusIdle}
                  >
                    {state}
                  </span>

                  {review?.submitted && (
                    <button
                      type="button"
                      style={styles.unsubmitBtn}
                      disabled={saving}
                      onClick={() => unsubmitReview(review)}
                    >
                      Unsubmit
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PeerReviewManager({
  course,
  peerReviewAssignments = [],
  allCourseAssignments = [],
  assignmentTypes = [],
  enrolments = [],
  orderedTypeIds = []
}) {
  if (peerReviewAssignments.length === 0) {
    return <div style={styles.muted}>No Peer Review instances configured.</div>;
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.intro}>
        Randomisation includes only students who actually submitted reviewable
        work to the target assignment. A non-submitter neither receives a
        review nor gets work to review unless you assign them manually below.
      </div>

      {peerReviewAssignments.map(assignment => (
        <PeerReviewInstanceManager
          key={assignment.id}
          course={course}
          assignment={assignment}
          allCourseAssignments={allCourseAssignments}
          assignmentTypes={assignmentTypes}
          enrolments={enrolments}
          orderedTypeIds={orderedTypeIds}
        />
      ))}
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  intro: {
    fontSize: '12px',
    lineHeight: 1.5,
    color: '#66788a',
    padding: '9px 11px',
    background: '#f7f9fa',
    border: '1px solid #e0e6ea',
    borderRadius: '8px'
  },
  instanceWrap: {
    border: '1px solid #dce3e8',
    borderRadius: '9px',
    padding: '12px',
    background: '#fff'
  },
  instanceHeading: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '10px'
  },
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 1fr)',
    gap: '12px',
    alignItems: 'end'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    fontSize: '12px',
    fontWeight: '800',
    color: '#334e68'
  },
  select: {
    width: '100%',
    border: '1px solid #cbd5dc',
    borderRadius: '7px',
    padding: '8px 9px',
    background: 'white',
    fontSize: '13px'
  },
  toggleField: {
    minHeight: '38px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '13px',
    color: '#334e68'
  },
  toggleHint: {
    display: 'block',
    marginTop: '2px',
    fontSize: '10px',
    fontWeight: '400',
    color: '#7a8894'
  },
  targetSummary: {
    marginTop: '10px',
    fontSize: '11px',
    color: '#66788a'
  },
  warningText: {
    color: '#8a5b00'
  },
  actions: {
    marginTop: '11px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  primaryBtn: {
    border: '1px solid #215b7d',
    background: '#215b7d',
    color: 'white',
    borderRadius: '7px',
    padding: '7px 10px',
    fontSize: '12px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  secondaryBtn: {
    border: '1px solid #cbd5dc',
    background: 'white',
    color: '#334e68',
    borderRadius: '7px',
    padding: '7px 10px',
    fontSize: '12px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  finalizedPill: {
    borderRadius: '999px',
    background: '#e7f6ed',
    color: '#276749',
    padding: '4px 9px',
    fontSize: '11px',
    fontWeight: '800'
  },
  allocationTable: {
    marginTop: '12px',
    border: '1px solid #e0e6ea',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 0.8fr) minmax(280px, 1.5fr) minmax(130px, 0.6fr)',
    gap: '10px',
    padding: '8px 9px',
    background: '#f4f7f9',
    color: '#52616d',
    fontSize: '11px',
    fontWeight: '800'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 0.8fr) minmax(280px, 1.5fr) minmax(130px, 0.6fr)',
    gap: '10px',
    alignItems: 'center',
    padding: '8px 9px',
    borderTop: '1px solid #edf1f4'
  },
  reviewerCell: {
    minWidth: 0,
    fontSize: '12px',
    color: '#334e68'
  },
  reviewerMeta: {
    marginTop: '2px',
    color: '#8a98a5',
    fontSize: '9px'
  },
  allocationSelect: {
    width: '100%',
    minWidth: 0,
    border: '1px solid #cbd5dc',
    borderRadius: '6px',
    padding: '6px 7px',
    background: 'white',
    fontSize: '11px'
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  statusIdle: {
    color: '#7a8894',
    fontSize: '10px',
    fontWeight: '700'
  },
  statusDraft: {
    color: '#8a5b00',
    fontSize: '10px',
    fontWeight: '800'
  },
  statusSubmitted: {
    color: '#276749',
    fontSize: '10px',
    fontWeight: '800'
  },
  unsubmitBtn: {
    border: '1px solid #d6a0a0',
    background: 'white',
    color: '#9a2c2c',
    borderRadius: '5px',
    padding: '3px 6px',
    fontSize: '9px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  muted: {
    color: '#7a8894',
    fontSize: '12px'
  },
  error: {
    marginBottom: '8px',
    padding: '8px 10px',
    background: '#fff4f4',
    border: '1px solid #efb7b7',
    borderRadius: '7px',
    color: '#912d2d',
    fontSize: '11px'
  },
  success: {
    marginBottom: '8px',
    padding: '8px 10px',
    background: '#f0faf4',
    border: '1px solid #b9dec7',
    borderRadius: '7px',
    color: '#276749',
    fontSize: '11px'
  }
};